// @flow
import {
  getBasePathModularUI,
  HTTP_METHODS,
  CONTENT_PATH,
} from "../../constants";

import Parameter from "../parameter/Parameter";

import type { LocationShape } from "react-router";
import type { ModelOptions } from "../types";

type HrefObject = {
  _path: string,
  _hash: string,
  _parameters: Array<Parameter>,
  _resourcetype: string,
  _method: $Keys<typeof HTTP_METHODS>,
  _state: ?{ [key: string]: any },
  pathname: ?string,
};

export type HrefInput = Href | HrefObject | LocationShape | string;

/**
 * Defines a Href with the parameters
 */
class Href {
  _path: string = "";
  _hash: string = "";
  _parameters: Array<Parameter> = [];
  _resourcetype: string = "";
  _method: $Keys<typeof HTTP_METHODS> = HTTP_METHODS.GET;
  _state: any = null;
  _referenceHash: ?number;
  _origin: string = "";
  _contextPath: string = getBasePathModularUI();

  /**
   * Create a Href
   */
  constructor(
    href?: HrefInput,
    resourcetype?: string,
    modelOptions?: ?ModelOptions,
  ) {
    this.resourcetype = resourcetype ?? "";

    if (href instanceof Href) {
      this.setFromHref(href);
    } else if (typeof href === "string") {
      this.setFromString(href);
    } else if (typeof href === "object") {
      this.setFromLocationOrObject(href);
    }

    if (typeof modelOptions?.origin === "string") {
      this.origin = modelOptions.origin;
    }

    if (typeof modelOptions?.contextPath === "string") {
      this.contextPath = modelOptions.contextPath;
    }
  }

  /**
   * Set parameters from Href model input
   */
  setFromHref(href: Href) {
    this.origin = href.origin;
    this.contextPath = href.contextPath;
    this.path = href.path;
    this.parameters = href.parameters;
    this.hash = href.hash;
    this.state = href.state;
  }

  /**
   */
  setFromLocationOrObject(href: LocationShape | HrefObject) {
    // $FlowFixMe
    if (typeof href._origin === "string") {
      this.origin = href._origin;
    }
    // $FlowFixMe
    if (typeof href._contextPath === "string") {
      this.contextPath = href._contextPath;
    }

    if (typeof href["pathname"] === "string") {
      this.path = href.pathname;
    } else if (typeof href._path === "string") {
      this.path = href._path;
    }

    if (href.search) {
      this.addParametersFromString(href.search);
    } else if (Array.isArray(href._parameters)) {
      // $FlowExpectedError[incompatible-call]
      href._parameters.map((param: Parameter) =>
        this.addParameter(param._name, param._value, param._prefix),
      );
    }

    if (href.hash) {
      this.hash = Href.getHashFromString(href.hash);
    } else if (typeof href._hash === "string") {
      this.hash = href._hash;
    }

    if (typeof href.state === "object") {
      this.setStateFromLocationOrObject(href.state);
    } else if (typeof href._state === "object") {
      this.setStateFromLocationOrObject(href._state);
    }
  }

  /**
   */
  setStateFromLocationOrObject(state?: any) {
    if (state != null) {
      this.state = state;
      if (typeof state.contextPath === "string") {
        this.contextPath = state.contextPath;
      }
      if (typeof state.origin === "string") {
        this.origin = state.origin;
      }
    }
  }

  /**
   * Set parameters from string input
   */
  setFromString(href: string) {
    this.path = Href.getPathFromString(href);
    this.addParametersFromString(href);
    this.hash = Href.getHashFromString(href);
  }

  /**
   */
  get isChangePassword(): boolean {
    return this._path.includes("/change-password");
  }

  /**
   * check if the path is referencing a camel route
   */
  static isCamelRoute(path: string): boolean {
    return (
      path.startsWith("/restServices") && !path.startsWith("/restServices/ui")
    );
  }

  /**
   */
  static checkAbsoluteUrl(href: string): boolean {
    const checkAbsolute = new RegExp("^(?:[a-z]+:)?//", "i");
    return checkAbsolute.test(href);
  }

  // Check if url is relative (not checking for absolute urls with same domain)
  /**
   */
  get isExternal(): boolean {
    return Href.checkAbsoluteUrl(this.path);
  }

  /**
   * Retrieve the relative path part of a href string, e.g. https://www.beinformed.com/BeInformed/tab/view?q=url => /tab/view
   */
  static getPathFromString(href: string): string {
    const hrefNoHash = href.includes("#")
      ? href.substring(0, href.indexOf("#"))
      : href;

    return hrefNoHash.includes("?") ? hrefNoHash.split("?")[0] : hrefNoHash;
  }

  /**
   * Retrieve hash of href string
   */
  static getHashFromString(href: string = ""): string {
    return href.includes("#") ? href.substring(href.indexOf("#") + 1) : "";
  }

  /**
   * Add a parameter for each parameter found in the querystring of an URL string, e.g. https://www.beinformed.com?q=url => q=url
   */
  addParametersFromString(href: string): Href {
    if (href.includes("?")) {
      href
        .split("?")[1]
        .split("&")
        .forEach((param) => {
          const paramFromString = Parameter.fromString(param);
          if (paramFromString) {
            this.setParameter(
              paramFromString.name,
              paramFromString.value,
              paramFromString.prefix,
            );
          }
        });
    }

    return this;
  }

  /**
   * Add a querystring parameter to the parameter collection of this Href, skips parameters that have a value of null
   */
  addParameter(name: string, value: ?string, prefix: ?string): Href {
    if (value !== null) {
      this.removeParameter(name, prefix);
      this._parameters.push(new Parameter(prefix, name, value));
    }

    return this;
  }

  /**
   * Adds or overwrites a parameter when it exists and value is not null.
   * Removes the parameter when the value is null
   */
  setParameter(name: string, value: ?string, prefix: ?string): Href {
    if (value === null) {
      this.removeParameter(name, prefix);
    } else {
      this.addParameter(name, value, prefix);
    }

    return this;
  }

  /**
   * Retrieve a parameter by it's name and (optionally) prefix
   */
  getParameter(name: string, prefix: ?string): ?Parameter {
    return this._parameters.find(
      (param) => param.prefix === prefix && param.name === name,
    );
  }

  /**
   */
  hasParameter(name: string, prefix: ?string): boolean {
    return this.getParameter(name, prefix) !== undefined;
  }

  /**
   * Get request method
   */
  get method(): $Keys<typeof HTTP_METHODS> {
    return this._method;
  }

  /**
   * Set request method
   */
  set method(method: $Keys<typeof HTTP_METHODS>) {
    this._method = method || HTTP_METHODS.GET;
  }

  /**
   * Retrieve all paremters
   */
  get parameters(): Array<Parameter> {
    return this._parameters;
  }

  /**
   * Replace parameters of Href
   */
  set parameters(parameters: Array<Parameter>) {
    this._parameters = parameters;
  }

  /**
   * Remove a parameter from the parameter collection
   */
  removeParameter(name: string, prefix: ?string, value: ?string): Href {
    if (value) {
      const valueToRemove = value;

      this._parameters = this._parameters.map((param) => {
        if (param.prefix === prefix && param.name === name) {
          const newValue = param.value
            ?.split(",")
            .filter((val) => val !== valueToRemove)
            .join(",");
          return new Parameter(param.prefix, param.name, newValue);
        }
        return param;
      });
    } else {
      this._parameters = this._parameters.filter(
        (param) => param.prefix !== prefix || param.name !== name,
      );
    }

    return this;
  }

  /**
   * Get the parameters as a querystring, e.g. param1=value1&param2=value2, optionally with prefix
   */
  getQuerystring(withPrefix: boolean = false): string {
    return this.parameters
      .filter((param) => param.value != null && param.value !== "")
      .map((param) => param.toQuerystring(withPrefix))
      .join("&");
  }

  /**
   * Retrieve a querystring that only contains parameter that can be send to the modular ui, parameters are filtered by prefix
   */
  getQuerystringForModularUI(prefix: string = ""): string {
    return this.parameters
      .filter(
        (param) =>
          param.isModUIParameter &&
          (!prefix || !param.prefix || param.prefix === prefix) &&
          param.value != null &&
          param.value !== "",
      )
      .map((param) => param.toQuerystring(false))
      .join("&");
  }

  /**
   * Retrieve all parameters from the parameter collection in a querystring style name1=value1&name2=value2, without the prefix
   */
  get querystring(): string {
    return this.getQuerystring(false);
  }

  /**
   * Retrieve all parameters from the parameter collection in a querystring style prefix~name1=value1&prefix~name2=value2, with the prefix
   * The prefix is used to identify parameters per component on a grouping panel
   * @returns {string}
   */
  get publicQuerystring(): string {
    return this.getQuerystring(true);
  }

  /**
   * Set the path of the Href, the part before the querystring question mark
   */
  set path(path: string) {
    this._path = path;
  }

  /**
   * Retrieve the path
   */
  get path(): string {
    return this._path ?? "";
  }

  /**
   * Retrieve the origin (e.g. http://example.com:18080)<br>
   * Empty when the default must be used
   */
  get origin(): string {
    return this._origin ?? "";
  }

  /**
   * Set the origin (e.g. http://example.com:18080)
   */
  set origin(origin: string) {
    this._origin = origin;
  }

  /**
   * Get the context path
   */
  get contextPath(): string {
    return this._contextPath;
  }

  /**
   * Set the context path if different from default
   */
  set contextPath(contextPath: string) {
    this._contextPath = contextPath;
  }

  /**
   * Set hash
   */
  set hash(hash: string) {
    this._hash = hash;
  }

  /**
   * Retrieve hash
   */
  get hash(): string {
    return this._hash;
  }

  /**
   */
  set state(state: any) {
    this._state = state;
  }

  /**
   */
  get state(): any {
    return this._state;
  }

  /**
   */
  setState(state: any): Href {
    this.state = state;

    return this;
  }

  /**
   */
  set formdata(formdata: any) {
    const currentState = this.state ?? {};

    this.state = {
      ...currentState,
      formdata,
    };
  }

  /**
   */
  get formdata(): any {
    return this.state?.formdata;
  }

  /**
   * Set resourctype
   */
  set resourcetype(resourcetype: string) {
    this._resourcetype = resourcetype;
  }

  /**
   * Retrieve resourceType
   */
  get resourcetype(): string {
    return this._resourcetype;
  }

  /**
   * Retrieve the path combined with the base path (context-path) of the application, e.g. /BeInformed
   */
  get absolutepath(): string {
    if (this.isExternal) {
      return this.path;
    }

    return this.origin + this.contextPath + this.path;
  }

  /**
   * Retrieves the combination of the path and the querystring
   */
  get href(): string {
    return this.querystring.length > 0
      ? [this.path, this.querystring].join("?")
      : this.path;
  }

  /**
   * Getting the URL including the base path, querystring is prefixed
   */
  get absolutehref(): string {
    const querystring = this.getQuerystring(false);

    return querystring.length > 0
      ? [this.absolutepath, querystring].join("?")
      : this.absolutepath;
  }

  /**
   * Getting the URL including the base path, the querystring is prefixed
   */
  get absolutehrefPrefixedQuerystring(): string {
    const querystring = this.getQuerystring(true);

    return querystring.length > 0
      ? [this.absolutepath, querystring].join("?")
      : this.absolutepath;
  }

  /**
   * Checks if the URL starts within the given href
   */
  startsWith(href: Href | string): boolean {
    const thisPath = `${this.path}/`;
    const otherPath = href instanceof Href ? `${href.path}/` : href;

    return thisPath.startsWith(otherPath);
  }

  /**
   * Checks if the given Href equals this Href
   */
  equals(href: Href | string | null): boolean {
    if (href instanceof Href) {
      return this.path === href.path;
    }

    return this.path === href;
  }

  /**
   */
  equalsWithParameters(other: Href | string | null): boolean {
    if (other == null) {
      return false;
    }

    const href = other instanceof Href ? other : new Href(other);

    if (!this.equals(href)) {
      return false;
    }

    if (this.parameters.length !== href.parameters.length) {
      return false;
    }

    if (this.parameters.length === 0 && href.parameters.length === 0) {
      return true;
    }

    const thisParams = this.parameters.map((param) => param.toString());

    return href.parameters.every((param) =>
      thisParams.includes(param.toString()),
    );
  }

  /**
   * Indicates if the link is a content link
   */
  get isContent(): boolean {
    return this.absolutepath.startsWith(
      `${getBasePathModularUI()}${CONTENT_PATH}`,
    );
  }

  /**
   */
  getReferenceHash(): ?number {
    return this._referenceHash;
  }

  /**
   */
  setReferenceHash(referenceHash: number) {
    this._referenceHash = referenceHash;
  }

  /**
   * Returns a complete url from the Href
   */
  toString(): string {
    return this.href;
  }

  /**
   */
  toLocation(): LocationShape {
    return {
      pathname: this.path,
      search:
        this.publicQuerystring.length > 0 ? `?${this.publicQuerystring}` : "",
      hash: this.hash.length > 0 ? `#${this.hash}` : "",
      state: {
        origin: this.origin,
        contextPath: this.contextPath,
        ...this.state,
      },
    };
  }
}

export default Href;
